import React from 'react';
import PdfHeaderComponent from './PdfHeaderComponent';
import { getProjectStatistics } from "../../../../apis/impact-report/report";
import useFetchData from "../../../../hooks/useFetchData";
import PdfHorizontalStackedBarChart from "./charts/PdfHorizontalStackedBarChart";
import Loader from "../../../../ui/Loader";
import { paginateHorizontalBarData, calculateChartDomain, generatePageTitle } from './utils/chartPagination';

const SixthPageComponent = ({ projectId, projectData }) => {
  // Fetch Contributions data
  const { loading: contributionLoading, response: contributionResponse } = useFetchData(
    getProjectStatistics,
    [projectId, "USER_WISE_STATISTICS", "", ""],
    [projectId, "USER_WISE_STATISTICS", "", ""]
  );

  // Transform Contributions data
  const contributionData = contributionResponse?.userWiseStatistics?.map(user => ({
    name: user?.username,
    participated: user?.bountyAcceptanceCount,
    created: user?.bountyCreationCount,
    validated: user?.bountyValidationCount,
    key: user?.uid,
    profileImage: user?.profileImage
  }))?.sort((a, b) => (b?.participated + b?.created + b?.validated) - (a?.participated + a?.created + a?.validated)) || [];


  // Normalize contribution data
  const normalizedContributionData = contributionData.map(row => ({
    ...row,
    participated: row.participated,
    created: row.created,
    validated: row.validated,
  }));

  // Paginate Contributions data
  const paginatedContributionData = paginateHorizontalBarData(normalizedContributionData, 27);
  const contributionDomainData = calculateChartDomain(normalizedContributionData);

  if (contributionLoading) {
    return (
      <div className="pdf-section pdf-page-break" style={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
        <PdfHeaderComponent projectTitle={projectData?.title} organizationName="Solarpunk Guild" />
        <div className='w-full h-[23.9375rem] flex pt-11 justify-center'>
          <Loader />
        </div>
      </div>
    );
  }

  if (paginatedContributionData.length === 0) {
    return (
      <div className="pdf-section pdf-page-break" style={{ display: 'flex', flexDirection: 'column', padding: '20px' }}>
        <PdfHeaderComponent projectTitle={projectData?.title} organizationName="Solarpunk Guild" />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Contributions</h2>
          <p className="text-xs text-gray-600 mb-3">User contributions breakdown by activity type</p>
          <div className="bg-grayBgNormal p-4 rounded-lg border border-gray-200 text-center text-gray-500" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="text-xs">No contributions data available</div>
          </div>
        </div>
      </div>
    );
  }

  // Render each page of Contributions data
  return (
    <>
      {paginatedContributionData.map((pageData, pageIndex) => {
        const pageNumber = pageIndex + 1;
        const totalPages = paginatedContributionData.length;
        const pageTitle = generatePageTitle("Contributions", pageNumber, totalPages);
        return (
          <div key={`contribution-page-${pageIndex}`} className="pdf-section pdf-page-break" style={{ display: 'flex', flexDirection: 'column', padding: '20px', height: '100vh' }}>
            <PdfHeaderComponent projectTitle={projectData?.title} organizationName="Solarpunk Guild" />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <h2 className="text-lg font-bold text-gray-900 mb-2">{pageTitle}</h2>
              <p className="text-xs text-gray-600 mb-3">Contribution breakdown by activity and type</p>
              
              <div className="bg-grayBgNormal p-4 rounded-lg " style={{ flex: 1, minHeight: '980px' }}>
                <PdfHorizontalStackedBarChart
                  data={pageData}
                  xAxisLabel="Contribution count"
                  yAxisLabel="Activities"
                  barSize={20}
                  showReferenceLine={false}
                  domain={contributionDomainData.domain}
                  ticks={contributionDomainData.ticks}
                  showLegend={pageIndex === 0}
                />
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default SixthPageComponent;
